import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { SendFilledIcon } from '@getgo/chameleon-icons/react';
import { TextField } from '@getgo/chameleon-material-ui';
import { IconButton } from '@getgo/chameleon-web-react-wrapper';
import classNames from 'classnames';
import Thumbnail from "../ConversationEvents/Thumbnail";
import i18n from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { useKeyDown, useTypingIndicatorEvents } from "../../utils/hooks";
import { KeyCodes } from "../../utils/keys";
import EmojiPicker from './EmojiPicker';
import FilePicker from './FilePicker';
import classes from './index.module.scss';
import PreSavedMessagePicker from './PreSavedMessagePicker';
import SurveyComplexPicker from './SurveyComplexPicker';
const MAX_LINES = 25;
/**
 * @deprecated Use ```@ucc-messaging/inbox-common-ui``` instead
 * */
const SendMessage = ({ message, canSend = true, className, focused, disabled, formFileBlobsWithId, color, placeholder, showEmoji, showSurveys, surveys, showPreSavedMessages = false, preSavedMessages, categories, showFiles, maxLines = MAX_LINES, pickerItems, onFocus, onBlur, onAdd, onRemove, onChange, onSend, onTyping, onSurveyPreview, onSurveyClicked, onCreateSurveyClicked, }) => {
    console.warn('SendMessage is deprecated, use @ucc-messaging/inbox-common-ui instead');
    const [rows, setRows] = useState(1);
    const [scroll, setScroll] = useState(false);
    const [psmSearch, setPsmSearch] = useState('');
    const [isPsmPickerOpen, setIsPsmPickerOpen] = useState(false);
    const timer = useRef(null);
    const textRef = useRef(null);
    useEffect(() => {
        if (textRef.current) {
            const element = textRef.current;
            const { lineHeight, paddingTop, paddingBottom } = getComputedStyle(element);
            const [computedLineHeight, computedPaddingTop, computedPaddingBottom] = [
                lineHeight,
                paddingTop,
                paddingBottom,
            ].map(value => (value && parseInt(value, 10)) || 0);
            timer.current = window.setTimeout(() => {
                setRows(1);
                const lines = Math.round((element.scrollHeight - (computedPaddingTop + computedPaddingBottom)) / computedLineHeight);
                setRows(Math.min(maxLines, lines));
                setScroll(lines > maxLines);
            }, 0);
            return () => {
                window.clearTimeout(timer.current);
                timer.current = undefined;
            };
        }
    }, [message]);
    useEffect(() => {
        focused && textRef.current.focus();
    }, [focused]);
    useKeyDown(() => {
        if (focused) {
            onChange('');
            document.activeElement.blur();
        }
        setIsPsmPickerOpen(false);
    }, [KeyCodes.ESCAPE]);
    useKeyDown(event => {
        if (focused && !event.shiftKey) {
            event.preventDefault();
            if (canSend && message.trim()) {
                handleSendMessage();
            }
        }
    }, [KeyCodes.ENTER]);
    const handleSendMessage = () => {
        onSend();
        setRows(1);
    };
    const handleEmojiClicked = emoji => {
        onChange(message + emoji);
        if (!focused) {
            textRef.current.focus();
        }
    };
    const handleMessageClicked = (message) => {
        onChange(message.message);
        if (!focused) {
            textRef.current.focus();
        }
        setIsPsmPickerOpen(false);
    };
    const handleSurveyClicked = (survey) => {
        onSurveyClicked === null || onSurveyClicked === void 0 ? void 0 : onSurveyClicked(survey);
        if (!focused) {
            textRef.current.focus();
        }
    };
    useEffect(() => {
        if (focused) {
            if (message.startsWith('/')) {
                setIsPsmPickerOpen(true);
                setPsmSearch(message.substr(1));
            }
            else {
                setIsPsmPickerOpen(false);
            }
        }
    }, [message, isPsmPickerOpen]);
    const onChangePsmSearch = (search) => setPsmSearch(search);
    const handlePsmIconClicked = () => setIsPsmPickerOpen(!isPsmPickerOpen);
    const sendDisabled = disabled || (!message && !(formFileBlobsWithId === null || formFileBlobsWithId === void 0 ? void 0 : formFileBlobsWithId.length)) || !canSend;
    const handleOnInput = useTypingIndicatorEvents({
        onTyping,
    });
    return (_jsx(TextField, { startAdornment: formFileBlobsWithId === null || formFileBlobsWithId === void 0 ? void 0 : formFileBlobsWithId.map(({ id, file }) => (_jsx(Thumbnail, { onRemove: () => onRemove(id), fileBlob: file, file: { name: file.name, size: file.size, contentType: file.type } }, id))), inputRef: textRef, className: classNames(classes.send, scroll && classes.scroll, (formFileBlobsWithId === null || formFileBlobsWithId === void 0 ? void 0 : formFileBlobsWithId.length) && classes.sendWithFiles, className), value: message, onChange: e => onChange(e.target.value), onFocus: onFocus, onBlur: onBlur, onInput: handleOnInput, fullWidth: true, disabled: disabled, rows: rows, inputProps: {
            spellCheck: true,
            lang: i18n.language,
        }, size: "area", placeholder: placeholder, endAdornment: _jsxs(_Fragment, { children: [pickerItems, showPreSavedMessages && canSend && (_jsx(PreSavedMessagePicker, { onMessageClicked: handleMessageClicked, preSavedMessages: preSavedMessages, categories: categories === null || categories === void 0 ? void 0 : categories.map(c => c.name), psmSearch: psmSearch, updatePsmSearch: onChangePsmSearch, onIconClicked: handlePsmIconClicked, onCloseClicked: () => setIsPsmPickerOpen(false), open: isPsmPickerOpen, disabled: disabled, color: color }, void 0)), showSurveys && canSend && (_jsx(SurveyComplexPicker, { onSurveyClicked: handleSurveyClicked, onSurveyPreviewClicked: survey => onSurveyPreview === null || onSurveyPreview === void 0 ? void 0 : onSurveyPreview(survey), onCreateSurveyClicked: onCreateSurveyClicked, surveys: surveys, disabled: disabled, color: color }, void 0)), showEmoji && (_jsx(EmojiPicker, { color: color, disabled: disabled, onClick: handleEmojiClicked }, void 0)), showFiles && (_jsx(FilePicker, { color: color, disabled: disabled, onAddFiles: mediaPayload => onAdd(mediaPayload) }, void 0)), _jsx(IconButton, Object.assign({ disabled: sendDisabled, onClick: handleSendMessage, size: "small" }, { children: _jsx(SendFilledIcon, { style: !sendDisabled && color && { color } }, void 0) }), void 0)] }, void 0) }, void 0));
};
export default SendMessage;
//# sourceMappingURL=index.js.map